import { RecipeIngredientType } from "../../../../config/enums/IngredientType.enum";
import {
  GenericIngredientOption,
  IngredientDraftRow,
  ProductUnitOption
} from "../types/recipeAddIngredient.types";
import { parseCompositeKey } from "../utils/recipeAddIngredient.utils";

export function createEmptyDraftRow(): IngredientDraftRow {
  return {
    ingredientType: "",
    productUnitKey: "",
    ingredientId: "",
    percentage: 0,
    descriptionOpen: false,
    saved: false
  };
}

export function isRowSaveable(draft: IngredientDraftRow): boolean {
  if (draft.saved || draft.isExisting) return false;
  if (!draft.ingredientType || Number(draft.percentage) < 0) return false;

  if (draft.ingredientType === RecipeIngredientType.OWN_PRODUCT) {
    return Boolean(draft.productUnitKey);
  }

  if (draft.ingredientType === RecipeIngredientType.GENERIC_INGREDIENT) {
    return Boolean(draft.ingredientId);
  }

  return false;
}

export function isRowEmpty(draft: IngredientDraftRow): boolean {
  return (
    !draft.ingredientType &&
    !draft.productUnitKey &&
    !draft.ingredientId &&
    (draft.percentage === "" || Number(draft.percentage) === 0)
  );
}

export function canRemoveRow(
  draft: IngredientDraftRow,
  pendingCount: number,
  hasSaved: boolean
): boolean {
  if (draft.saved) return false;
  if (pendingCount === 1 && isRowEmpty(draft) && !hasSaved) return false;
  return true;
}

export function validatePercentage(value: any): boolean | string {
  const num = value === "" || value === null ? 0 : Number(value);
  if (num > 100) {
    return "Percentage cannot exceed 100%";
  }
  return true;
}

interface OwnProductSubmitData {
  productId: string;
  salesUnitId: string;
}

interface GenericIngredientSubmitData {
  ingredientId: string;
  ingredientName: string;
}

export type IngredientSubmitData = OwnProductSubmitData | GenericIngredientSubmitData;

export function buildIngredientSubmitDto(
  draft: IngredientDraftRow,
  productUnitOptions: ProductUnitOption[],
  genericOptions: GenericIngredientOption[]
): IngredientSubmitData | null {
  if (!isRowSaveable(draft)) return null;

  if (draft.ingredientType === RecipeIngredientType.OWN_PRODUCT) {
    const parsed = parseCompositeKey(draft.productUnitKey);
    if (!parsed) return null;
    const [productId, salesUnitId] = parsed;
    return { productId, salesUnitId };
  }

  if (draft.ingredientType === RecipeIngredientType.GENERIC_INGREDIENT) {
    const ingredient = genericOptions.find(
      option => String(option.value) === String(draft.ingredientId)
    );
    return {
      ingredientId: draft.ingredientId,
      ingredientName: ingredient?.title ?? ""
    };
  }

  return null;
}
